import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, Check, RotateCw } from 'lucide-react';

const ImageCropper = ({ 
  isOpen, 
  onClose, 
  imageSrc, 
  onCropComplete,
  aspectRatio = 1, // 1 = square for profile pics, 16/9 for covers etc
  circularCrop = false,
  title = "Crop Image"
}) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    aspect: aspectRatio,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    
    // Center the crop area
    const cropWidth = Math.min(80, (height / width) * 80);
    const cropHeight = aspectRatio ? cropWidth / aspectRatio : cropWidth;
    
    const initialCrop = {
      unit: '%',
      width: cropWidth,
      height: cropHeight,
      x: (100 - cropWidth) / 2,
      y: (100 - cropHeight) / 2,
      aspect: aspectRatio,
    };
    
    setCrop(initialCrop);
    
    // CRITICAL FIX: Also set completedCrop with pixel values so that
    // getCroppedImg() works even if the user doesn't adjust the crop area.
    // This was the bug - if user clicked Save without moving the crop,
    // completedCrop was null and the image wouldn't be saved.
    const pixelCrop = {
      x: (initialCrop.x / 100) * width,
      y: (initialCrop.y / 100) * height,
      width: (initialCrop.width / 100) * width,
      height: (initialCrop.height / 100) * height,
      unit: 'px',
    };
    setCompletedCrop(pixelCrop);
  }, [aspectRatio]);

  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    console.log('[ImageCropper] getCroppedImg called:', {
      displayWidth: image.width,
      displayHeight: image.height,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      scaleX,
      scaleY,
      completedCrop
    });

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Set canvas size to crop size - scale from display coords to natural coords
    const pixelCrop = {
      x: completedCrop.x * scaleX,
      y: completedCrop.y * scaleY,
      width: completedCrop.width * scaleX,
      height: completedCrop.height * scaleY,
    };

    console.log('[ImageCropper] Scaled pixelCrop:', pixelCrop);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // If circular crop, make it circular
    if (circularCrop) {
      ctx.globalCompositeOperation = 'destination-in';
      ctx.beginPath();
      ctx.arc(
        pixelCrop.width / 2,
        pixelCrop.height / 2,
        Math.min(pixelCrop.width, pixelCrop.height) / 2,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    // Return as base64
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          resolve(reader.result);
        };
      }, 'image/jpeg', 0.9);
    });
  }, [completedCrop, circularCrop]);

  const handleSave = async () => {
    const croppedImage = await getCroppedImg();
    console.log('[ImageCropper] Cropped image result:', croppedImage ? `${croppedImage.substring(0, 50)}...` : 'null');
    if (croppedImage) {
      onCropComplete(croppedImage);
    } else {
      console.error('[ImageCropper] Failed to crop image');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Crop Area */}
        <div className="p-4 bg-gray-900 flex items-center justify-center min-h-[300px]">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspectRatio}
            circularCrop={circularCrop}
            className="max-h-[400px]"
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop preview"
              onLoad={onImageLoad}
              className="max-h-[400px] object-contain"
              crossOrigin="anonymous"
            />
          </ReactCrop>
        </div>

        {/* Actions - Touch Friendly */}
        <div className="flex gap-3 p-4">
          <button
            onClick={onClose}
            className="flex-1 py-4 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors active:scale-95 touch-manipulation"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 py-4 rounded-xl bg-purple-500 text-white font-semibold hover:bg-purple-600 transition-colors flex items-center justify-center gap-2 active:scale-95 touch-manipulation"
          >
            <Check className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
